// ============================================================
// CHANGELOG CONSTANTS — Metawhale
// ============================================================
// How to add a new version:
//   1. Add a new entry at the TOP of the `CHANGELOGS` array.
//   2. Set `status` to "preview" while on staging, "beta" for
//      soft-launched features, or omit for fully released.
//   3. Use `type` per-item:
//        "new"        → brand new feature users will notice
//        "improved"   → enhancement to existing functionality
//        "fixed"      → bug fix (user-facing description)
//        "deprecated" → removed or sunset feature
//   4. Use `devNote` (optional) for QA/dev context: ticket refs,
//      technical details, affected areas, known edge cases.
//
// How to add a WIP item:
//   Add to the `WORK_IN_PROGRESS` array at the bottom.
//   Remove it once it ships and add it as a proper CHANGELOGS entry.
// ============================================================

export type ChangeType = "new" | "improved" | "fixed" | "deprecated";
export type VersionStatus = "preview" | "beta" | "coming-soon";

export interface ChangeItem {
  type: ChangeType;
  title: string;
  description: string;
  devNote?: string; // QA/dev context — not shown to regular users (optional)
}

export interface ChangelogEntry {
  version: string;
  date: string; // ISO format: "YYYY-MM-DD"
  status?: VersionStatus; // omit if fully live/production
  summary: string; // one-liner shown in the header
  changes: ChangeItem[];
}

export interface WipItem {
  title: string;
  description: string;
  devNote?: string;
}

// ─────────────────────────────────────────────────────────────
// WORK IN PROGRESS
// Items actively being developed. Move to CHANGELOGS on release.
// ─────────────────────────────────────────────────────────────
export const WORK_IN_PROGRESS: WipItem[] = [
  // {
  //   title: "Quadmedia Usage — Visual Updates",
  //   description:
  //     "Updating the content and layout of the quadmedia usage list for a clearer, more informative breakdown of media activity across print, broadcast, online, and social.",
  //   devNote: "Visual/content changes to the quadmedia usage list view.",
  // },
];

// ─────────────────────────────────────────────────────────────
// CHANGELOGS
// Newest version at the top.
// ─────────────────────────────────────────────────────────────
export const CHANGELOGS: ChangelogEntry[] = [
  // ─────────────────────────────────────────────────────────
  // STAGING / PREVIEW
  // ─────────────────────────────────────────────────────────
  {
    version: "0.32.1-alpha",
    date: "2026-04-13",
    status: "preview",
    summary: "Navbar layout adjustment for plans view.",
    changes: [
      {
        type: "improved",
        title: "Navbar Logo Position in Plans",
        description:
          "The logo position in the navbar has been adjusted within the plans view for better layout balance and visual alignment.",
        devNote:
          "Updated navbar layout specifically for plans page to reposition logo.",
      },
    ],
  },
  {
    version: "0.32.0-alpha",
    date: "2026-04-13",
    status: "preview",
    summary:
      "API key copy action, AI developer tooling, and performance-focused container refactors.",
    changes: [
      {
        type: "improved",
        title: "API Key Copy Button",
        description:
          "A copy button has been added to the API key card launcher, making it faster and easier to copy keys directly from the interface.",
        devNote: "Added copy action to the API key card launcher component.",
      },
      {
        type: "new",
        title: "AI Codebase Mindmap Skill",
        description:
          "An internal Claude skill has been created to scan the codebase and generate a developer-focused mindmap for faster onboarding, better context sharing, and AI-assisted development workflows.",
        devNote:
          "Built a custom Claude skill that analyzes the project structure and outputs a codebase mindmap primarily for Obsidian-based documentation and AI context.",
      },
      {
        type: "improved",
        title: "Container Render Performance Optimizations",
        description:
          "Several container components have been optimized to reduce unnecessary re-renders and improve overall interface responsiveness.",
        devNote:
          "Wrapped event handlers in useCallback, converted renderContent/renderBottomContent patterns to useMemo constants, and stabilized export handler references across PulseCreateContainer, PulseInfoContainer, InfluencerContainer, KeywordContainer, SourceContainer, and PulseContainer.",
      },
      {
        type: "improved",
        title: "More Efficient Two-Column Layout Rendering",
        description:
          "Shared layout rendering has been improved to work more efficiently with memoized components, reducing avoidable updates during parent re-renders.",
        devNote:
          "Updated TwoColumnLayout integration so memoized children receive stable props. Removed dead props like monitoringType from cardLauncherProps and cleaned up unused data flow.",
      },
      {
        type: "fixed",
        title: "Results Panel Typing Cleanup",
        description:
          "Results panel internals have been cleaned up to improve type safety and prevent unsafe error handling behavior.",
        devNote:
          "Removed unused data prop from ResultsPanel and changed error handling from any to unknown with instanceof Error narrowing.",
      },
      {
        type: "improved",
        title: "Codebase Cleanup",
        description:
          "Internal cleanup was completed across multiple components to improve maintainability and reduce unused or misleading code.",
        devNote:
          'Removed stale comments, deleted unnecessary "use client" directives in the Vite codebase, cleaned dead imports, and tightened shared component typing.',
      },
    ],
  },
  {
    version: "0.31.2-alpha",
    date: "2026-04-07",
    status: "preview",
    summary: "Improved mutation payload structure and post data consistency.",
    changes: [
      {
        type: "improved",
        title: "Pulse Mutation Payload Update",
        description:
          "Pulse-related actions now use a clearer payload structure, improving data handling and maintainability.",
        devNote:
          "Updated mutation to accept { pulseId, postUrl } instead of previous parameters.",
      },
      {
        type: "improved",
        title: "Automatic Post Data Refresh",
        description:
          "Post data now refreshes automatically after mutations, ensuring the interface always reflects the latest updates.",
        devNote: "Invalidates post data query on successful mutation.",
      },
    ],
  },
  {
    version: "0.31.1-alpha",
    date: "2026-04-07",
    status: "preview",
    summary: "Performance optimizations for asset loading and data fetching.",
    changes: [
      {
        type: "improved",
        title: "Faster Asset Loading",
        description:
          "Key SVG assets are now preloaded to load in parallel with API requests, reducing delays and improving perceived performance.",
        devNote:
          "Preloaded SVGs by rendering hidden images during loading to avoid sequential fetch after API response.",
      },
      {
        type: "improved",
        title: "Reduced Unnecessary Refetching",
        description:
          "Data fetching has been optimized to prevent redundant requests, resulting in smoother navigation and fewer loading flashes.",
        devNote:
          "Added staleTime to queries to avoid immediate refetch on revisit.",
      },
    ],
  },
  {
    version: "0.31.0-alpha",
    date: "2026-03-31",
    status: "preview",
    summary: "Pulse sync fix, UI polish, and added interface animations.",
    changes: [
      {
        type: "fixed",
        title: "Pulse Sync Endpoint Error",
        description:
          "Resolved an issue where syncing Pulse data would fail due to a server validation error.",
        devNote:
          "Fixed 422 Unprocessable Content error in Pulse PUT endpoint (sync button).",
      },
      {
        type: "fixed",
        title: "Footer Text Layout",
        description:
          "Fixed an issue where footer text was incorrectly split into two lines, improving readability and layout consistency.",
        devNote: "Adjusted footer layout styling.",
      },
      {
        type: "improved",
        title: "Improved Tab Interactions",
        description:
          "Tabs now show a pointer cursor on hover, making interactive elements clearer and more intuitive.",
        devNote: "Added cursor-pointer styling to tab components.",
      },
      {
        type: "improved",
        title: "Interface Animations",
        description:
          "Added subtle animations to the logo, subtitle, and page load transitions for a smoother and more engaging user experience.",
        devNote:
          "Implemented animations for branding elements and initial page load.",
      },
      {
        type: "improved",
        title: "Documentation Updates",
        description:
          "Documentation has been updated to reflect recent changes and improvements across the platform.",
        devNote: "General documentation updates.",
      },
    ],
  },
  {
    version: "0.29.0-alpha",
    date: "2026-03-24",
    status: "preview",
    summary:
      "Routing reliability improvements, build fixes, and standardized date formatting.",
    changes: [
      {
        type: "improved",
        title: "Stronger Protected Routing",
        description:
          "Access control for protected routes has been strengthened, ensuring more reliable enforcement of authentication and user permissions across the application.",
        devNote: "Enhanced protected route handling and validation logic.",
      },
      {
        type: "fixed",
        title: "TanStack Router Build Error",
        description:
          "Resolved an issue that caused build failures related to the routing setup, improving deployment stability.",
        devNote:
          "Fixed TanStack Router configuration causing build-time errors.",
      },
      {
        type: "improved",
        title: "Standardized Date Formatting",
        description:
          "Date formatting across the application is now consistent, improving readability and reducing formatting discrepancies.",
        devNote:
          "Created shared formatDate utility and replaced existing date formatting implementations across multiple files.",
      },
      {
        type: "improved",
        title: "Codebase Integration Updates",
        description:
          "Internal refactors have been merged to streamline the codebase and improve overall maintainability.",
        devNote: "Merged refactor branch into main development flow.",
      },
    ],
  },
  {
    version: "0.28.3-alpha",
    date: "2026-03-24",
    status: "preview",
    summary:
      "Centralized color system and eliminated redundant styling across the codebase.",
    changes: [
      {
        type: "improved",
        title: "Centralized Theme Variables",
        description:
          "New theme variables have been introduced to standardize colors across monitoring, admin, and dark mode interfaces, improving visual consistency.",
        devNote:
          "Added 11 CSS variables in @theme (monitoring, admin/dark, accent) in src/index.css.",
      },
      {
        type: "improved",
        title: "Shared Monitoring Badge Styles",
        description:
          "Monitoring badge styles are now unified, ensuring consistent appearance across all monitoring-related views.",
        devNote:
          "Extracted MONITORING_BADGE_STYLES into src/constants/monitoring.ts, removing ~14 duplicate definitions across 15 files.",
      },
      {
        type: "improved",
        title: "Reduced Hardcoded Colors",
        description:
          "Hardcoded color values have been replaced with standardized utility classes and theme variables, resulting in a cleaner and more maintainable styling system.",
        devNote:
          "Replaced ~20 hex colors across ~65 files using Tailwind utilities and newly introduced CSS variables.",
      },
    ],
  },
  {
    version: "0.28.2-alpha",
    date: "2026-03-19",
    status: "preview",
    summary: "Service status moved from footer to in-app views.",
    changes: [
      {
        type: "improved",
        title: "Relocated Service Status Display",
        description:
          "Service status is no longer shown in the footer and is now integrated into private pages and the sidebar, making it more visible and contextually relevant for logged-in users.",
        devNote:
          "Removed footer status component and integrated status display into authenticated layouts and sidebar.",
      },
    ],
  },
  {
    version: "0.28.1-alpha",
    date: "2026-03-18",
    status: "preview",
    summary:
      "Client plan display improvements, NotFound page updates, and major codebase standardization.",
    changes: [
      {
        type: "new",
        title: "Client Plan Segment Display",
        description:
          "Client views now support displaying parsed plan segments, making subscription details clearer and easier to understand.",
        devNote:
          "Implemented plan segment parsing and integrated display logic in client-related components.",
      },
      {
        type: "new",
        title: "Reintegrated NotFound Page",
        description:
          "A dedicated NotFound page has been restored to handle invalid routes, improving navigation feedback and user experience.",
        devNote:
          "Reintroduced and integrated NotFound page into routing system.",
      },
      {
        type: "improved",
        title: "Responsive Subscription Overview",
        description:
          "Subscription details now adapt more intelligently, displaying tier information in place of quota where appropriate for better clarity.",
        devNote:
          "Updated subscription overview logic to conditionally replace quota display with tier.",
      },
      {
        type: "improved",
        title: "Monitoring Module Naming Standardization",
        description:
          "Monitoring-related modules have been renamed for clearer and more consistent terminology across the platform.",
        devNote:
          "Renamed keyword/influencer/source-based modules to *-monitoring pattern.",
      },
      {
        type: "improved",
        title: "Codebase Structure & Naming Consistency",
        description:
          "The project structure has been standardized to improve maintainability, readability, and developer experience.",
        devNote:
          "Aligned legal/doc exports, standardized component filenames to PascalCase, added Container suffix to route components, moved Pagination to Common, extracted date utilities, added barrel exports, and fixed casing imports.",
      },
      {
        type: "deprecated",
        title: "Removed Legacy Influencer Modules",
        description:
          "Obsolete influencer-related APIs, components, and store files have been removed as part of ongoing cleanup.",
        devNote: "Deleted unused influencer-related modules.",
      },
      {
        type: "improved",
        title: "Project Configuration Updates",
        description:
          "Internal project configuration and documentation files have been updated for better development workflow.",
        devNote:
          "Initialized claude.md, added to .gitignore, and removed duplicate Login page.",
      },
    ],
  },
  {
    version: "0.28.0-alpha",
    date: "2026-03-13",
    status: "preview",
    summary:
      "Client deactivation workflow, improved request handling, and performance refinements.",
    changes: [
      {
        type: "new",
        title: "Client Deactivation with Confirmation",
        description:
          "Administrators can now deactivate clients directly from the client management interface. A confirmation dialog is shown before deactivation to prevent accidental actions.",
        devNote:
          "Added reusable ConfirmDialog component, implemented deactivateClient API and useDeactivateClient mutation, and integrated logic into ClientDetailContainer and ClientContainer (supports bulk deactivation).",
      },
      {
        type: "improved",
        title: "Client Plan Segmentation",
        description:
          "Client filtering and display logic has been enhanced to better support plan-based segmentation, making it easier to manage and review client subscriptions.",
        devNote:
          "Updated client filtering and display logic to support new plan segmentation.",
      },
      {
        type: "improved",
        title: "Requests & Manager Overview Performance",
        description:
          "Internal request handling and data prefetching have been optimized for improved responsiveness and cleaner code structure.",
        devNote:
          "Simplified skip calculation in RequestsContainer (removed unnecessary useMemo) and refactored ManagerOverview prefetching to use Promise.all.",
      },
      {
        type: "improved",
        title: "Accessibility Improvement",
        description:
          "Improved accessibility by ensuring important images include descriptive attributes.",
        devNote: "Added alt attribute to Stripe logo image in OrderSummary.",
      },
    ],
  },
  {
    version: "0.27.2-alpha",
    date: "2026-03-06",
    status: "preview",
    summary: "Refined hover feedback in select dropdown items.",
    changes: [
      {
        type: "improved",
        title: "Subtle Hover Background for Select Items",
        description:
          "Hover feedback for select dropdown items has been softened to create a cleaner and less distracting interaction.",
        devNote: "Adjusted SelectItem hover background opacity from 30% to 5%.",
      },
    ],
  },
  {
    version: "0.27.1-alpha",
    date: "2026-03-06",
    status: "preview",
    summary: "Improved interaction behavior in source media carousel.",
    changes: [
      {
        type: "fixed",
        title: "Prevent Image Dragging in Source Carousel",
        description:
          "Images inside the source media carousel can no longer be dragged accidentally, improving the browsing experience when navigating through media items.",
        devNote:
          "Disabled default browser drag behavior for images in SourceCarousel to prevent unintended drag interactions.",
      },
    ],
  },
  {
    version: "0.27.0-alpha",
    date: "2026-03-06",
    status: "preview",
    summary:
      "Influencer matching improvements, link validation, CSV exports, and smarter result handling.",
    changes: [
      {
        type: "new",
        title: "Influencer Link Validation",
        description:
          "Influencer links are now validated before launching searches. Invalid or duplicate URLs are visually flagged, helping users quickly identify and correct issues.",
        devNote:
          "Added link normalization and validation utilities. InfluencerTable now highlights invalid/duplicate links, and InfluencerContainer disables actions when validation fails.",
      },
      {
        type: "improved",
        title: "Smarter Influencer Matching Queries",
        description:
          "Influencer matching logic has been optimized to reduce unnecessary data fetching, resulting in a faster and more responsive experience when editing rows or preparing a launch.",
        devNote:
          "Optimized query key in useMatchedInfluencers to only depend on hasLaunched. Validation results are now passed as options to control query conditions.",
      },
      {
        type: "improved",
        title: "Empty Results Detection & Safer Actions",
        description:
          "When no results are returned, the interface now clearly indicates the empty state and disables related actions to prevent confusion.",
        devNote:
          "Introduced isEmptyResults logic and propagated it through ResultsPopover and ResultsPanel. Buttons are disabled when responseJSON is missing or empty.",
      },
      {
        type: "improved",
        title: "CSV Export for Results",
        description:
          "Influencer and keyword results can now be exported as CSV files for easier compatibility with spreadsheets and data tools.",
        devNote:
          "Replaced XLSX export logic with CSV utilities and updated components to use the new export handlers.",
      },
      {
        type: "improved",
        title: "Keyword Launch Safeguards",
        description:
          "The launch workflow now provides clearer feedback when keywords are missing or platform selections are incomplete, preventing accidental empty launches.",
        devNote:
          "Integrated useKeywordsStore in KeywordsContainer. Launch button state now depends on selected platforms, subfields, and keyword presence.",
      },
    ],
  },
  {
    version: "0.26.0-alpha",
    date: "2026-03-06",
    status: "preview",
    summary:
      "Enhanced client management with improved edit mode behavior and plan handling.",
    changes: [
      {
        type: "improved",
        title: "Client Edit Mode Plan Management",
        description:
          "Client editing now has improved behavior when managing subscription plans. Administrators can view all available plans while preventing unintended tier switching during edits.",
        devNote:
          "Added isEditMode prop to MonitoringTabs and MonitoringTabContent to control plan selection behavior.",
      },
      {
        type: "improved",
        title: "Improved Client Data Consistency After Edits",
        description:
          "Client updates now refresh more related data automatically, ensuring the interface reflects changes immediately after edits.",
        devNote:
          "Extended query invalidation logic in useEditClient to refresh additional dependent queries.",
      },
      {
        type: "improved",
        title: "Simplified Client Plan Filtering Logic",
        description:
          "Plan filtering logic has been streamlined to make plan display more predictable while maintaining safeguards during edit mode.",
        devNote:
          "Refactored useClientPlans logic to always show all plans while restricting tier switching during edits.",
      },
    ],
  },
  {
    version: "0.25.5-alpha",
    date: "2026-03-05",
    status: "preview",
    summary: "Minor UI text clarification and layout alignment fixes.",
    changes: [
      {
        type: "fixed",
        title: "Corrected Deactivation Button Label",
        description:
          'Updated the button text from "Deleting..." to "Deactivating..." for better clarity and accuracy when disabling hash keys.',
        devNote: "Modified HashKeysActionButtons loading state label.",
      },
      {
        type: "improved",
        title: "Improved Team Management Layout Alignment",
        description:
          "Adjusted spacing and margins in team management views to improve visual alignment and create a more consistent layout.",
        devNote:
          "Updated margin values in TeamListHeader and TeamListRow components.",
      },
    ],
  },
  {
    version: "0.25.4-alpha",
    date: "2026-03-05",
    status: "preview",
    summary: "Changelog corrections.",
    changes: [
      {
        type: "fixed",
        title: "Changelog Entry Corrections",
        description:
          "Minor corrections were made to previously published changelog entries to ensure accuracy and consistency.",
        devNote:
          "Adjusted changelog records for correctness and formatting consistency.",
      },
    ],
  },
  {
    version: "0.25.3-alpha",
    date: "2026-03-05",
    status: "preview",
    summary: "Improved footer link interactions.",
    changes: [
      {
        type: "improved",
        title: "Footer Link Hover Animation",
        description:
          "Footer links now feature a smooth underline animation on hover, providing clearer visual feedback and a more polished interactive experience.",
        devNote:
          "Added underline-on-hover animation styling to footer link components.",
      },
    ],
  },
  {
    version: "0.25.2-alpha",
    date: "2026-03-05",
    status: "preview",
    summary:
      "Visual refinements to client icons and improved platform sorting.",
    changes: [
      {
        type: "improved",
        title: "Refined Icon Sizing in Client Views",
        description:
          "Platform and navigation icons in client-related views have been adjusted for better visual balance and consistency. Typography weight has also been softened for a cleaner look.",
        devNote:
          "Updated icon classes from text-lg to text-md and added font-light in ClientNavigation and ClientDetailContainer.",
      },
      {
        type: "improved",
        title: "Alphabetical Platform Icon Sorting",
        description:
          "Platforms displayed in subscription views are now sorted alphabetically, making them easier to scan and more predictable in layout.",
        devNote:
          "Sorted platforms before mapping in Subscription and ManagerSubscription components.",
      },
    ],
  },
  {
    version: "0.25.1-alpha",
    date: "2026-03-03",
    status: "preview",
    summary:
      "Service status now includes proper loading states and skeleton UI.",
    changes: [
      {
        type: "improved",
        title: "ServiceStatus Loading State & Skeleton UI",
        description:
          "The Service Status component now displays a proper loading state with skeleton placeholders while data is being fetched. This creates a smoother and more consistent user experience.",
        devNote:
          "Added isLoading prop to ServiceStatus. Implemented skeleton UI elements and updated ServiceStatusContainer to pass loading state correctly.",
      },
    ],
  },
  {
    version: "0.25.0-alpha",
    date: "2026-02-27",
    status: "preview",
    summary:
      "Improved token refresh reliability and unified loading indicators across the platform.",
    changes: [
      {
        type: "improved",
        title: "Simplified Token Refresh Flow",
        description:
          "Token refresh handling has been streamlined to improve reliability and error visibility. Authentication errors are now handled more gracefully, with clearer logout behavior and better recovery during refresh failures.",
        devNote:
          "Removed coordinatedRefresh and deleted refreshLock.ts. Integrated direct API refresh calls into axios interceptors and useAutoRefreshToken. Added cooldown handling and improved logging for refresh lifecycle.",
      },
      {
        type: "new",
        title: "Unified Spinner Component",
        description:
          "A reusable Spinner component has been introduced to standardize loading indicators across the application. This creates a more consistent and polished user experience during loading states.",
        devNote:
          "Replaced legacy loading animations across components. Added spinner keyframes in CSS and refactored loading states to use the shared Spinner component.",
      },
    ],
  },
  {
    version: "0.24.0-alpha",
    date: "2026-02-27",
    status: "preview",
    summary:
      "Role-based routing, authentication guards, and major token management refactor.",
    changes: [
      {
        type: "new",
        title: "Role-Based Routing System",
        description:
          "Metawhale now supports structured role-based routing with dedicated public, protected, manager, and admin route layers. Access is now clearly separated based on user roles, improving security and navigation clarity.",
        devNote:
          "Introduced dedicated route files for public, protected, manager, and admin views. Integrated with new auth guard utilities.",
      },
      {
        type: "new",
        title: "Authentication Guards & Role Utilities",
        description:
          "Authentication and role validation are now enforced consistently across the platform. Admin and Manager permissions are validated before accessing restricted areas.",
        devNote:
          "Added authGuards.ts with requireAuth, requireAdmin, and requireManager helpers.",
      },
      {
        type: "improved",
        title: "Coordinated Token Refresh System",
        description:
          "Token refresh handling is now more reliable and efficient. The system prevents duplicate refresh requests and reduces unexpected logouts or race conditions.",
        devNote:
          "Implemented refreshLock.ts for coordinated refresh requests. Refactored axios interceptors and auto-refresh logic to centralize token lifecycle management.",
      },
      {
        type: "improved",
        title: "Sidebar & State Management Refactor",
        description:
          "Sidebar state handling has been improved for better reliability and consistency across the application.",
        devNote:
          "Replaced SidebarContext with useSidebarStore and standardized token hook imports.",
      },
      {
        type: "improved",
        title: "Enhanced Query & Client Editing Logic",
        description:
          "Client editing and keyword queries have been refined to improve mutation handling and data invalidation. This results in more accurate updates and smoother UI state transitions.",
        devNote:
          "Standardized token hook import paths, added useQueryClient to keyword queries, updated editClient to accept userId, improved useEditClient payload structure, and enhanced ClientAddContainer edit flow.",
      },
    ],
  },
  {
    version: "0.23.1-alpha",
    date: "2026-02-27",
    status: "preview",
    summary: "Client list layout improvements and cleaner component structure.",
    changes: [
      {
        type: "improved",
        title: "Refined Client List Layout",
        description:
          "Client list components have been visually refined for better alignment and readability. Layout improvements using grid and flexbox create a cleaner and more structured presentation across cards, headers, and rows.",
        devNote:
          "Updated ClientCard, ClientListHeader, and ClientListRow layout structure.",
      },
      {
        type: "improved",
        title: "Cleaner Component Structure",
        description:
          "Underlying component structure has been simplified to improve maintainability and consistency, reducing unnecessary wrappers and improving code clarity.",
        devNote:
          "Removed redundant divs and comments to streamline JSX structure.",
      },
      {
        type: "improved",
        title: "Consistent Subscription End Date Formatting",
        description:
          "Subscription end dates in the client list are now formatted consistently for clearer visibility and better date readability.",
        devNote: "Added shared date formatting logic inside ClientListRow.",
      },
    ],
  },
  {
    version: "0.23.0-alpha",
    date: "2026-02-27",
    status: "preview",
    summary:
      "Client editing is now supported, with updated subscription plans and smarter validation.",
    changes: [
      {
        type: "new",
        title: "Edit Client — Admin",
        description:
          "Administrators can now edit existing client accounts directly from the dashboard. Update personal details, modify subscription plans, and manage credentials without recreating the account.",
        devNote:
          "Implemented editClient API, added useEditClient mutation hook, and extended ClientAddContainer to support edit mode. Handles password and plan updates.",
      },
      {
        type: "improved",
        title: "Updated Subscription Plans",
        description:
          "Subscription plans have been updated to reflect new pricing and expanded platform support, ensuring plan details are clearer and more aligned with current offerings.",
        devNote:
          "Adjusted subscription plan configuration to support revised pricing and platform mappings.",
      },
      {
        type: "improved",
        title: "Conditional Password Validation",
        description:
          "Password fields are now required only when necessary. When editing a client, passwords are optional unless being changed, improving usability for administrators.",
        devNote:
          "Enhanced form validation logic to conditionally require password based on edit mode.",
      },
    ],
  },
  {
    version: "0.22.3-alpha",
    date: "2026-02-27",
    status: "preview",
    summary:
      "Improved date formatting consistency and email usability in Requests.",
    changes: [
      {
        type: "improved",
        title: "Unified Activity Timestamp Formatting",
        description:
          "Dates in Request views are now formatted consistently using a shared utility function, ensuring clearer and more readable timestamps across request lists and detail panels.",
        devNote:
          "Replaced manual formatting logic in RequestDetailPanel and RequestListRow with formatActivityTimestamp utility.",
      },
      {
        type: "improved",
        title: "Clickable Email in Request Details",
        description:
          "Email addresses in the Request detail panel are now clickable, allowing users to quickly initiate communication.",
        devNote:
          "Converted static email display to a mailto link in RequestDetailPanel.",
      },
    ],
  },
  {
    version: "0.22.2-alpha",
    date: "2026-02-27",
    status: "preview",
    summary: "Client detail view now uses structured first and last names.",
    changes: [
      {
        type: "improved",
        title: "Client Name Display Update",
        description:
          "Client detail pages now display first and last names separately for better clarity and consistency across the platform. Breadcrumb titles and overview sections have been updated to reflect the new structured name format.",
        devNote:
          "Updated ClientDetailContainer breadcrumb construction and ClientOverview display. API response types refactored to use first_name and last_name instead of a single full name field.",
      },
    ],
  },
  {
    version: "0.22.1-alpha",
    date: "2026-02-26",
    status: "preview",
    summary: "Quadmedia Usage — Visual Updates",
    changes: [
      {
        type: "improved",
        title: "Manager-side Source Monitoring UI Updates",
        description:
          "The quota for source monitoring is now displayed properly.",
        devNote:
          "Replaced the outdated quota display with the usage tier information.",
      },
    ],
  },
  {
    version: "0.22.0-alpha",
    date: "2026-02-26",
    status: "preview",
    summary: "Changelog page is now live.",
    changes: [
      {
        type: "new",
        title: "Changelog Page",
        description:
          "You're looking at it. Metawhale now has a dedicated changelog so you can stay up to date on new features, improvements, and fixes as they ship.",
      },
    ],
  },
  {
    version: "0.21.0-alpha",
    date: "2026-02-26",
    status: "preview",
    summary:
      "Admin user management, UI consistency fixes, and a critical monitoring scrape fix.",
    changes: [
      {
        type: "new",
        title: "Add Users — Admin",
        description:
          "Administrators can now create new user accounts directly from the Admin panel. Set credentials, assign platforms, and manage access from one place without needing a separate onboarding flow.",
        devNote: "Admin-only route.",
      },
      {
        type: "improved",
        title: "Checkbox Disabled State",
        description:
          "Disabled checkboxes now have a more visually distinct appearance, making it clearer when an option is unavailable. Previously, disabled and enabled states looked too similar.",
        devNote:
          "Affects checkboxes across the platform — most visible in Keyword Monitoring keyword list.",
      },
      {
        type: "fixed",
        title: "Monitoring Scrape Failure Due to Incorrect API Token",
        description:
          "Some monitoring jobs were silently failing to scrape content due to an internal configuration issue. Affected monitors will now collect data correctly.",
        devNote:
          "Root cause: a secondary API token (not exposed in the API key selector UI) was being used for certain monitoring type mappings, causing entity_type mismatches and insufficient permission errors on scrape. Fixed by correcting the monitoring type-to-token mapping.",
      },
    ],
  },
];
